import { useMemo, useState } from 'react';
import { Box, FormControlLabel, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

import dayjs from '../../../../config/dayjs';
import { BlockTitle, PrimitiveValue, humanizeKey } from './ReportViewer';

const DATE_FORMAT = 'DD MMM YYYY, hh:mm A';
const MUTED = '#98A2B3';
const CHANGED_BACKGROUND = '#FFFAEB';
const GENERAL_SECTION = 'general';
// Parts of a report that change by themselves between two reports of an unchanged machine (audit
// run, running processes, device paths, link state, DHCP addresses...): the same list central
// ignores when it records hardware changes (deviceAuditService VOLATILE_KEYS).
const SKIPPED_KEYS = new Set([
    'audit', 'collection', 'sourceCreatedAt', 'sourceUpdatedAt', 'processes', 'devicePath', 'pid',
    'link', 'ip', 'downloadSpeed', 'currentFrequencyMHz', 'health', 'timestamp',
]);
const SKIPPED_PATHS = ['cpu.flags'];
// What sets the seat's previous hardware apart from its current (change from getPreviousHardware).
const CHANGES = {
    machine_replaced: 'Another machine was at this seat before',
    parts_changed: 'Parts of this machine were changed',
    report_format_changed: 'The report comes from a newer version of the audit',
    settings_changed: 'Settings or peripherals of this machine changed',
};

const formatDate = (value) => (value ? dayjs(value).format(DATE_FORMAT) : null);
const displayValue = (value) => (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value);

// Every value of a report as { section, path, label, value }: one row per field, arrays of objects
// numbered (Devices #1 › Model), arrays of plain values joined.
const flattenReport = (report) => {
    const rows = [];
    const walk = (value, path, labels, section) => {
        if (SKIPPED_PATHS.includes(path)) return;
        if (Array.isArray(value)) {
            if (!value.length || value.every((item) => item === null || typeof item !== 'object')) {
                rows.push({ section, path, label: labels.join(' › '), value: value.join(', ') });
                return;
            }
            value.forEach((item, index) => walk(item, `${path}[${index}]`, [...labels, `#${index + 1}`], section));
            return;
        }
        if (value !== null && typeof value === 'object') {
            const entries = Object.entries(value).filter(([key]) => !SKIPPED_KEYS.has(key));
            if (!entries.length) rows.push({ section, path, label: labels.join(' › '), value: null });
            entries.forEach(([key, child]) => walk(child, `${path}.${key}`, [...labels, humanizeKey(key)], section));
            return;
        }
        rows.push({ section, path, label: labels.join(' › '), value: displayValue(value) });
    };
    for (const [key, value] of Object.entries(report ?? {})) {
        if (SKIPPED_KEYS.has(key)) continue;
        const isPart = value !== null && typeof value === 'object';
        walk(value, key, isPart ? [] : [humanizeKey(key)], isPart ? key : GENERAL_SECTION);
    }
    return rows;
};

const sameValue = (a, b) => String(a ?? '') === String(b ?? '');

// Sections of current and previous side by side: { key, name, rows: [{ label, current, previous, changed }], changed }.
const compareReports = (current, previous) => {
    const sections = new Map();
    const rowOf = (row) => {
        if (!sections.has(row.section)) sections.set(row.section, new Map());
        const rows = sections.get(row.section);
        if (!rows.has(row.path)) rows.set(row.path, { path: row.path, label: row.label || humanizeKey(row.section), current: undefined, previous: undefined });
        return rows.get(row.path);
    };
    flattenReport(current).forEach((row) => { rowOf(row).current = row.value; });
    flattenReport(previous).forEach((row) => { rowOf(row).previous = row.value; });
    return [...sections.entries()].map(([key, rows]) => {
        const list = [...rows.values()].map((row) => ({ ...row, changed: Boolean(previous) && !sameValue(row.current, row.previous) }));
        return { key, name: key === GENERAL_SECTION ? 'General' : humanizeKey(key), rows: list, changed: list.filter((row) => row.changed).length };
    });
};

const headCell = { fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap', bgcolor: '#F9FAFB' };

const SectionTable = ({ rows, withPrevious }) => (
    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table size='small'>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ ...headCell, width: '34%' }}>Field</TableCell>
                    <TableCell sx={headCell}>Current</TableCell>
                    {withPrevious && <TableCell sx={headCell}>Previous</TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.path ?? row.label} sx={{ bgcolor: row.changed ? CHANGED_BACKGROUND : 'transparent', '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top', wordBreak: 'break-word' }}>{row.label}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top', fontWeight: row.changed ? 600 : 400 }}><PrimitiveValue value={row.current} /></TableCell>
                        {withPrevious && <TableCell sx={{ verticalAlign: 'top' }}><PrimitiveValue value={row.previous} /></TableCell>}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

// Who the current and the previous hardware belong to, side by side.
const SnapshotHeader = ({ current, previous }) => {
    const items = [
        ['Device ID', current.deviceId, previous?.deviceId],
        ['MAC address', current.macAddress, previous?.macAddress],
        ['Hardware fingerprint', current.fingerprint, previous?.hardwareFingerprint],
        // differs with every new report of the same machine: not a change in the hardware
        ['Report collected', formatDate(current.hardwareAt), formatDate(previous?.hardwareAt), false],
    ];
    return <SectionTable withPrevious={Boolean(previous)}
        rows={items.map(([label, now, before, canChange = true]) => ({ label, current: now, previous: before, changed: canChange && Boolean(previous) && !sameValue(now, before) }))} />;
};

// What the previous hardware is (basis from getPreviousHardware); a seat's earlier machine says so itself.
const comparedWith = (basis, previous) => {
    if (basis === 'approval') return `Compared with the hardware at approval on ${formatDate(previous?.createdAt)}.`;
    if (basis === 'first_seen') return `Compared with the hardware first recorded on ${formatDate(previous?.createdAt)}.`;
    return '';
};

// The device's current hardware next to the hardware to compare it with (previous: the audit row
// from getPreviousHardware, or null; change: what differs; basis: what previous is), every field
// of the report, changed fields highlighted.
export const HardwareComparison = ({ current, previous, change, basis, state }) => {
    const previousReport = previous?.hardware ?? null;
    const sections = useMemo(() => compareReports(current.hardware, previousReport), [current.hardware, previousReport]);
    const changedFields = sections.reduce((total, section) => total + section.changed, 0);
    const [changesOnly, setChangesOnly] = useState(true);
    const [open, setOpen] = useState(() => new Set());
    const showChangesOnly = Boolean(previousReport) && changedFields > 0 && changesOnly;
    const shown = showChangesOnly
        ? sections.filter((section) => section.changed).map((section) => ({ ...section, rows: section.rows.filter((row) => row.changed) }))
        : sections;
    const isOpen = (key) => (showChangesOnly ? !open.has(key) : open.has(key));
    const toggle = (key) => setOpen((keys) => {
        const next = new Set(keys);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    let summary = 'No earlier hardware recorded for this device or seat.';
    if (state === 'loading') summary = 'Looking for earlier hardware at this seat…';
    else if (state === 'error') summary = 'The earlier hardware could not be loaded.';
    else if (previousReport) {
        const differ = changedFields ? `${changedFields} fields differ, in ${sections.filter((section) => section.changed).length} sections.` : 'No field of the report differs.';
        summary = [comparedWith(basis, previous), change ? `${CHANGES[change] ?? 'The hardware changed'}: ${differ}` : 'The hardware has not changed since.'].filter(Boolean).join(' ');
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <BlockTitle>Hardware: current and previous</BlockTitle>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                <Typography variant='body2' sx={{ color: 'text.secondary', flex: 1, minWidth: 240 }}>{summary}</Typography>
                {Boolean(previousReport) && changedFields > 0 && (
                    <FormControlLabel control={<Switch size='small' checked={changesOnly} onChange={(event) => { setChangesOnly(event.target.checked); setOpen(new Set()); }} />}
                        label={<Typography variant='body2'>Changed fields only</Typography>} />
                )}
            </Box>
            <SnapshotHeader current={current} previous={previous} />

            {!Object.keys(current.hardware ?? {}).length && !previousReport
                ? <Typography variant='body2' sx={{ color: MUTED }}>This device has not sent a hardware report.</Typography>
                : shown.map((section) => (
                    <Box key={section.key} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                        <Box role='button' tabIndex={0} onClick={() => toggle(section.key)}
                            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggle(section.key); } }}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer', bgcolor: isOpen(section.key) ? 'primary.50' : 'transparent' }}>
                            <Box component='span' sx={{ fontSize: 11, color: MUTED, width: 12 }}>{isOpen(section.key) ? '▾' : '▸'}</Box>
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>{section.name}</Typography>
                            <Box component='span' sx={{ ml: 'auto', px: 1, borderRadius: 10, fontSize: 12, ...(section.changed
                                ? { color: '#93370D', bgcolor: CHANGED_BACKGROUND, border: '1px solid #FEDF89' }
                                : { color: 'text.secondary', bgcolor: '#F2F4F7' }) }}>
                                {section.changed ? `${section.changed} changed` : `${section.rows.length} fields`}
                            </Box>
                        </Box>
                        {isOpen(section.key) && <Box sx={{ p: 1.5, pt: 0.5 }}><SectionTable rows={section.rows} withPrevious={Boolean(previousReport)} /></Box>}
                    </Box>
                ))}

        </Box>
    );
};
